import { NavigationProp, useNavigation } from "@react-navigation/native";
import { Button, CheckBox, Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import dayjs from "dayjs";
import {
  ChargeDetails,
  GroupTicketDetails,
  SeatDetails,
  SimplePlanPackageDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { MyEventsStackParamList } from "navigation/types";
import i18n from "plugins/localization";
import React from "react";
import { View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import { getUserName } from "utils/userUtils";
import TicketAction from "./components/TicketAction";
import { isTicketReservedByGuestComponent } from "./helpers";
interface SeatInfoProps {
  groupCode: string;
  tickets: TicketDetails[];
  groupTickets: GroupTicketDetails[];
  releaseTicket: (ticket: TicketDetails[]) => void;
  reservedByAccountId: number;
  planPackage: SimplePlanPackageDetails | null;
  selectionMode?: boolean;
  selectedTicketIds?: Set<number>;
  onToggleTicket?: (ticket: TicketDetails) => void;
  onEnterSelectionMode?: () => void;
  onExitSelectionMode?: () => void;
  onReleaseSelected?: () => void;
}

const getFormattedAmountPaid = (charge: ChargeDetails) => {
  const {
    priceType,
    totalCreditCardCharges,
    totalPrice,
    totalPoints,
    totalCredits,
  } = charge;
  if (priceType === "CURRENCY") {
    return formatCurrency(totalPrice);
  } else {
    let totalCreditUsage = 0;
    let parsedTotalCreditCardCharges = Number(totalCreditCardCharges);
    let parsedTotalCredits = Number(totalCredits);
    const formattedTotalPoints = formatCurrency(totalPoints, {
      priceType: priceType,
    });
    // price type is POINTS
    if (parsedTotalCreditCardCharges > 0 || parsedTotalCredits > 0) {
      totalCreditUsage = parsedTotalCreditCardCharges + parsedTotalCredits;
    }
    if (!totalCreditUsage) {
      return formattedTotalPoints;
    } else {
      return `${formattedTotalPoints} + ${formatCurrency(totalCreditUsage)}`;
    }
  }
};
const SeatInfo: React.FC<SeatInfoProps> = ({
  groupCode,
  tickets,
  groupTickets,
  releaseTicket,
  reservedByAccountId,
  planPackage,
  selectionMode = false,
  selectedTicketIds,
  onToggleTicket,
  onEnterSelectionMode,
  onExitSelectionMode,
  onReleaseSelected,
}) => {
  const { account } = useAccount();

  const navigation =
    useNavigation<NavigationProp<MyEventsStackParamList, "EventDetail">>();

  const withinReleaseWindow = (ticket: TicketDetails) => {
    const subscriptions = account!.subscriptions;
    let releaseWindowMinutes: number = 0;

    for (const subscription of subscriptions) {
      for (const component of subscription.planPackage.components) {
        if (component.id === ticket.planPackageComponentId) {
          releaseWindowMinutes = component.releaseWindowMinutes;
        }
      }
    }

    return dayjs().isBefore(
      dayjs(ticket.event.localDate)
        .tz(ticket.event.timezone)
        .subtract(releaseWindowMinutes, "minutes"),
    );
  };

  const shouldShowRelease = (ticket: TicketDetails) => {
    // For ASSOCIATE account, show the release button, since ticket could have been claimed by the account holder.
    if (!planPackage) return true;

    if (planPackage.eventCancelDisabled) return false;
    // AXS does not support releasing single seats from a reservation.
    // So if TenantOptions.ticketProvider = AXS, we will need to remove the individual "X" graphics from the tickets and hide them.
    if (ticket.provider === "AXS") return false;
    // TFL does not support releasing single seats from a reservation.
    if (ticket.provider === "TFL") return false;
    if (ticket.status !== "RESERVED") return false;
    if (isTicketReservedByGuestComponent(ticket)) {
      return true;
    }
    const accountHasGuestPasses = groupTickets.some(
      (groupTicket) =>
        groupTicket.planPackageComponentType?.includes("GUEST") &&
        groupTicket.reservedByAccountId === account?.id,
    );

    // if account has more than 1 non-guest tickets for current event, we allow them to release one at a time
    if (accountHasGuestPasses) {
      /**
       * Frank cannot release his ticket regardless of transfer status.
       * He cannot release his own single ticket, because he booked guest passes for the event.
       * Doesn't matter who is holding them.
       * He can delete his entire reservation (as long as he still holds all the guest passes in his account and they haven't been transferred yet),
       * or he has to release the guest pass prior to releasing his own ticket.
       */
      return (
        groupTickets.filter(
          (groupTicket) =>
            !groupTicket.planPackageComponentType?.includes("GUEST") &&
            groupTicket.accountId === account?.id &&
            groupTicket.status === "RESERVED" &&
            withinReleaseWindow(ticket),
        ).length > 1
      );
    }

    return withinReleaseWindow(ticket);
  };
  const handleReleaseBtnPress = (ticket: TicketDetails) => {
    releaseTicket([ticket]);
  };

  const releasableCount = tickets.filter((t) => shouldShowRelease(t)).length;
  const showSelectButton = releasableCount >= 2;
  const selectedCount = selectedTicketIds?.size ?? 0;

  const renderSeatInfo = (
    seat: SeatDetails,
    ticket: TicketDetails | GroupTicketDetails,
  ) => {
    const { sectionDisplay, seatDisplay, rowDisplay, ga } = seat;
    const isTicketDetails = "provider" in ticket;
    // Always show seat for group ticket, or if ticket provider is not TFL
    const shouldShowSeat =
      !isTicketDetails || (isTicketDetails && ticket.provider !== "TFL");

    if (isTicketDetails && "EXPERIENCE" === ticket.event.type) {
      return <></>;
    }

    if (isTicketDetails && "RSVP" === ticket.event.type) {
      return (
        <View>
          <Text category="p2" style={{ marginVertical: 3 }}>
            {sectionDisplay}
          </Text>
        </View>
      );
    }

    return (
      <View>
        {ga ? (
          <Text category="p2">
            {i18n.t("common.section") as string}: {sectionDisplay}
          </Text>
        ) : (
          <View>
            {/* Don't show seat for TFL */}
            <Text category="p2" style={{ marginVertical: 3 }}>
              {`Section: ${sectionDisplay} Row: ${rowDisplay}`}
              {shouldShowSeat ? ` Seat: ${seatDisplay}` : ""}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const renderDescription = (ticket: TicketDetails) => {
    const { transferCode, charge } = ticket;
    return (
      <View>
        {renderSeatInfo(ticket.seat, ticket)}
        {charge && ticket.reservedByAccountId === account?.id && (
          <Text category="c1">
            {i18n.t("screens.EventDetail.amountPaid", {
              amount: getFormattedAmountPaid(charge),
            })}
          </Text>
        )}
        {transferCode &&
          // guest pass cannot be transferred
          !isTicketReservedByGuestComponent(ticket) && (
            <Text
              style={{
                fontWeight: "bold",
                color: "red",
                fontSize: 12,
              }}
            >
              {i18n.t("screens.EventDetail.voucherTransferInitiated")}
            </Text>
          )}
      </View>
    );
  };

  // filter out tickets that belong to the the current logged in account
  const filterAccountTicket = (ticket: GroupTicketDetails) => {
    return ticket.accountId !== account?.id;
  };

  const toManageGuestScreen = () => {
    navigation.navigate("Guests", { groupCode });
  };

  // If any of the group tickets don't have the same reservedByAccountId
  // as the reservedByAccountId prop, then it is a group reservation.
  // For guest passes, the reservedByAccountId would be the same
  // as the reservedByAccountId prop.
  const isGroupReservation = groupTickets.some(
    (ticket) => ticket.reservedByAccountId !== reservedByAccountId,
  );

  return (
    <View>
      <View>
        <BaseCard>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text category="h6">{i18n.t("screens.EventDetail.attendees")}</Text>
            {showSelectButton && (
              <Button
                size="tiny"
                appearance={selectionMode ? "ghost" : "outline"}
                status={selectionMode ? "basic" : "primary"}
                onPress={
                  selectionMode ? onExitSelectionMode : onEnterSelectionMode
                }
              >
                {selectionMode ? "Cancel" : "Select"}
              </Button>
            )}
          </View>
          {tickets.map((ticket) => {
            const isGuestTicket =
              isTicketReservedByGuestComponent(ticket) &&
              ticket.reservedByAccountId === ticket.accountId;
            const isReleasable = shouldShowRelease(ticket);
            return (
              <BaseCardListItem
                key={ticket.id}
                title={
                  <Text style={{ fontWeight: "700" }}>
                    {ticket.user
                      ? getUserName(ticket.user!)
                      : `${ticket.planPackageComponentDisplayName || ticket.planPackageComponentName}`}
                  </Text>
                }
                onPress={
                  selectionMode
                    ? isReleasable
                      ? () => onToggleTicket?.(ticket)
                      : undefined
                    : isGuestTicket
                      ? toManageGuestScreen
                      : undefined
                }
                description={renderDescription(ticket)}
                accessoryRight={
                  selectionMode ? (
                    <CheckBox
                      checked={selectedTicketIds?.has(ticket.id) ?? false}
                      onChange={() => onToggleTicket?.(ticket)}
                      disabled={!isReleasable}
                    />
                  ) : (
                    <TicketAction
                      onReleaseBtnPress={() => handleReleaseBtnPress(ticket)}
                      shouldShowRelease={isReleasable}
                      isGroupReservation={isGroupReservation}
                      allowGroupCodeReleases={
                        ticket.event.allowGroupCodeReleases
                      }
                    />
                  )
                }
              ></BaseCardListItem>
            );
          })}
          {selectionMode && selectedCount > 0 && (
            <View
              style={{
                marginTop: 12,
                paddingHorizontal: 16,
                paddingBottom: 12,
              }}
            >
              <Button size="small" onPress={onReleaseSelected}>
                {`Release ${selectedCount} ${i18n.t("common.seat", { count: selectedCount })}`}
              </Button>
            </View>
          )}
          {groupTickets.filter(filterAccountTicket).map((groupTicket) => {
            let title = "";
            if (
              groupTicket.accountId === groupTicket.reservedByAccountId &&
              groupTicket.planPackageComponentType?.includes("GUEST")
            ) {
              title = i18n.t("screens.EventDetail.guestPending");
            } else {
              title = `${groupTicket.firstName} ${groupTicket.lastName}`;
            }

            return (
              <BaseCardListItem
                key={groupTicket.id}
                title={<Text style={{ fontWeight: "700" }}>{title}</Text>}
                description={renderSeatInfo(groupTicket.seat, groupTicket)}
                accessoryRight={<></>}
              ></BaseCardListItem>
            );
          })}
        </BaseCard>
      </View>
    </View>
  );
};

export default SeatInfo;
